import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Alert } from './Alert';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** Extra content — a reason field, a list of what will be affected. */
  children?: ReactNode;
  /**
   * Why the last confirm failed. Shown inside the dialog: an error set on the
   * page behind it is hidden by the open dialog, so the click looks ignored.
   */
  error?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything that suspends, archives or deletes. */
  tone?: 'primary' | 'danger';
  /**
   * Replaces the danger banner's "it can be restored later" for an action that
   * cannot be undone from the product. Only read when `tone` is `danger`.
   */
  dangerNote?: ReactNode;
  isLoading?: boolean;
  /** Disable confirm until the caller's own condition is met. */
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * One dialog for every "are you sure" in the product. Consistent wording and
 * button order matter here — a suspend action must never look like a save.
 */
export function ConfirmDialog({
  isOpen,
  title,
  description,
  children,
  error,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  dangerNote,
  isLoading = false,
  isConfirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const hasBody = Boolean(error) || tone === 'danger' || children != null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isConfirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {/* Nothing at all when there is nothing to say: an empty body still
          renders its padding, a blank band between the header and footer. */}
      {hasBody && (
        <>
          {error && (
            <div className="mb-4">
              <Alert tone="critical">{error}</Alert>
            </div>
          )}
          {/*
            A red banner on the destructive ones.

            The title already names what is about to happen, but a wall of plain
            text reads the same whether it is saving a class or suspending a
            student. This says which kind of dialog it is before a word is read —
            and says out loud that the record survives, which is the question
            somebody hesitating over this button is actually asking.
          */}
          {tone === 'danger' && (
            <div className="mb-4 flex items-start gap-3 rounded-field border border-crit bg-crit-bg p-3">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-crit" aria-hidden />
              <p className="text-sm text-ink">
                {dangerNote ?? (
                  <>
                    <span className="font-[560] text-crit">This suspends the record.</span> Nothing
                    is deleted — it can be restored later.
                  </>
                )}
              </p>
            </div>
          )}

          {children}
        </>
      )}
    </Modal>
  );
}
